<template>
  <div>
    <v-row
      dense
      align="center"
    >
      <v-spacer />
      <v-col cols="auto">
        <date-selector
          v-model="date"
          :label="$t('picker.date.title')"
        />
      </v-col>
    </v-row>
    <today-wellness
      class="mt-2"
      :stats="latestTeamStats"
      :loading="teamStats.loading"
      view="team"
    />
    <v-card class="mt-4 rounded-lg">
      <v-card-title>
        <v-row align="center">
          <v-col>
            {{ $t("table_title.players") }}
          </v-col>
          <v-col cols="auto">
            <v-select
              v-model="playerPositions.selectedId"
              solo
              dense
              :label="$t('table_headers.player_overview.position')"
              hide-details
              clearable
              item-value="id"
              item-text="name"
              :items="playerPositions.data"
              style="width: 200px"
            />
          </v-col>
          <v-col
            cols="auto"
            class="pl-2"
          >
            <v-select
              v-model="playerStatuses.selectedId"
              solo
              dense
              :label="$t('table_headers.player_overview.status')"
              hide-details
              clearable
              :items="playerStatuses.data"
              item-value="id"
              item-text="name"
              style="width: 200px"
            />
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="tableHeaders"
          :items="filteredSearchedPlayers"
          :loading="players.loading"
          disable-pagination
          hide-default-footer
          :items-per-page="-1"
          sort-by="last_name"
          :sort-desc="false"
        >
          <template v-slot:progress>
            <v-skeleton-loader
              class="my-2"
              type="table-row-divider@15"
            />
          </template>
          <template v-slot:item="{item}">
            <tr
              style="cursor: pointer"
              @click="navigateTo(item.id)"
            >
              <td
                style="width: 0px"
                class="pr-0"
              >
                <v-avatar
                  size="32px"
                  class="mr-2"
                  color="grey lighten-2"
                >
                  <v-img :src="item.photo_url">
                    <template v-slot:placeholder>
                      <v-icon x-small>
                        fal fa-user
                      </v-icon>
                    </template>
                  </v-img>
                </v-avatar>
              </td>
              <td>
                <div class="d-flex align-center">
                  <div class="flex-grow-1">
                    {{ item.last_name }} {{ item.first_name }}
                  </div>
                  <div
                    v-if="hasSmartAssistantModule"
                    class="d-flex align-center justify-center ml-2"
                    style="width: 24px; min-width: 24px; cursor: pointer;"
                    @click.stop.prevent="$router.push({name: RouteNames.PLAYER_SMART_ASSISTANT, params: {id: item.id}})"
                  >
                    <v-tooltip
                      v-if="item.smart_assistant && item.smart_assistant.length"
                      top
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-badge
                            :content="item.smart_assistant.length"
                            color="primary"
                            inline
                            :value="item.smart_assistant.length"
                          />
                        </span>
                      </template>
                      <span>{{ $tc('tooltip.smart_assistant', item.smart_assistant.length, { num: item.smart_assistant.length }) }}</span>
                    </v-tooltip>

                    <v-tooltip
                      v-else
                      top
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <span
                          v-bind="attrs"
                          v-on="on"
                        >
                          <v-icon
                            color="success"
                            size="22px"
                          >
                            fas fa-circle-check
                          </v-icon>
                        </span>
                      </template>
                      <span>{{ $t("smart_assistant.no_alerts") }}</span>
                    </v-tooltip>
                  </div>
                </div>
              </td>
              <td>
                <v-chip
                  v-for="position in item.player_positions.slice(0, 3)"
                  :key="item.id + position.id"
                  small
                  label
                  class="mx-1"
                >
                  {{ position.short_name }}
                </v-chip>
              </td>
              <td>
                <table-selection-chip-value
                  :value="item.latest_stats.FAT"
                  :max="5"
                  reverse-colors
                />
              </td>
              <td style="width: 0">
                <table-selection-chip-value
                  :value="item.latest_stats.GMS"
                  :max="5"
                  reverse-colors
                />
              </td>
              <td style="width: 0">
                <table-selection-chip-value
                  :value="item.latest_stats.STL"
                  :max="5"
                  reverse-colors
                />
              </td>
              <td>
                <table-selection-chip-value
                  :value="item.latest_stats.MOO"
                  :max="5"
                />
              </td>
              <td>
                <table-selection-chip-value
                  :value="item.latest_stats.SLQ"
                  :max="5"
                />
              </td>
              <td>
                <table-bar-value
                  :value="item.latest_stats.SLD"
                  :max="8"
                  color="grey lighten-2"
                >
                  {{ item.latest_stats.SLD }}
                </table-bar-value>
              </td>
              <td>
                <pain-label
                  :value="item.latest_stats.pain_max_DL"
                  :player-stats="item.latest_stats"
                  :gender="item.gender"
                />
              </td>
              <td>
                <sickness-label :player-stats="item.latest_stats" />
              </td>
              <td v-if="showMenstrualPeriod">
                <div
                  v-if="item.latest_stats.FMENSACT !== undefined"
                >
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-chip
                        label
                        outlined
                        small
                        :color="item.latest_stats.FMENSACT ? 'blue' : 'grey'"
                        v-on="on"
                      >
                        {{ item.latest_stats.FMENSACT ? $t('status.yes') : $t('status.no') }}
                        <span
                          v-if="item.latest_stats.FMENSFR"
                          class="ml-1"
                        >
                          ({{ item.latest_stats.FMENSFR }})
                        </span>
                      </v-chip>
                    </template>
                    <div>
                      <div v-if="item.latest_stats.FMENSFR">
                        {{ $t('individual_analysis.menstrual_period.variable.flow_rate') }} <strong>{{ item.latest_stats.FMENSFR }}</strong>
                      </div>
                      <div v-if="item.latest_stats.last_menstrual_period_start">
                        Last period started: <strong>{{ $dayjs(item.latest_stats.last_menstrual_period_start).format('L') }}</strong>
                      </div>
                      <div v-if="item.latest_stats.last_menstrual_period_end">
                        Last period ended: <strong>{{ $dayjs(item.latest_stats.last_menstrual_period_end).format('L') }}</strong>
                      </div>
                    </div>
                  </v-tooltip>
                </div>
                <div
                  v-else
                  class="grey--text caption"
                >
                  N/A
                </div>
              </td>
              <td v-if="showMenstrualPeriod">
                <menstrual-phases
                  v-if="item.latest_stats.cycle_phase && item.latest_stats.cycle_phase !== noPhase"
                  :current-menstrual-phase="item.latest_stats.cycle_phase"
                  :stats="item.latest_stats"
                />
                <span
                  v-else
                  class="grey--text caption"
                >
                  N/A
                </span>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-row class="mt-4">
      <v-col>
        <section class="fill-height">
          <team-pain-widget
            :today-stats="latestTeamStats"
            :yesterday-stats="yesterdayTeamStats"
          />
        </section>
      </v-col>
      <v-col>
        <wellness-overview-chart
          :stats="latestTeamStats"
          height="350"
        />
      </v-col>
    </v-row>
    <v-row class="mt-4">
      <v-col cols="12">
        <sleep-quality-sleep-duration-chart
          :players="players.data"
          style="height: 450px"
        />
      </v-col>
      <v-col cols="12">
        <mood-stress-chart
          :players="players.data"
          style="height: 450px"
        />
      </v-col>
      <v-col cols="12">
        <general-muscle-soreness-chart
          :players="players.data"
          style="height: 450px"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>

import { PlayerApiService, PlayerPositionApiService, PlayerStatusApiService, TeamApiService } from '@/data/services'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import { processMaxValues, processMinValues, toCamelCase } from '@/utils/helpers'
import { get } from 'lodash'
import PainLabel from '@/components/helpers/PainLabel.vue'
import TableBarValue from '@/components/helpers/TableBarValue.vue'
import TableSelectionChipValue from '@/components/helpers/TableSelectionChipValue.vue'
import SicknessLabel from '@/components/helpers/SicknessLabel.vue'
import MenstrualPhases from '@/components/helpers/MenstrualPhases.vue'
import { MenstrualPhase } from '@/data/models'
import { RouteNames } from '@/router/routes'
import TeamPainWidget from '@/components/training-load/TeamPainWidget.vue'
import TodayWellness from '@/components/wellness/TodayWellness.vue'
import DateSelector from '@/components/DateSelector.vue'
import WellnessOverviewChart from '@/components/charts/implementation/WellnessOverviewChart.vue'
import SleepStatusChart from '@/components/charts/implementation/SleepStatusChart.vue'
import MoodStressChart from '@/components/charts/implementation/MoodStressChart.vue'
import GeneralMuscleSorenessChart from '@/components/charts/implementation/GeneralMuscleSorenessChart.vue'

export default {
  name: 'WellnessView',
  components: {
    GeneralMuscleSorenessChart,
    MoodStressChart,
    SleepQualitySleepDurationChart: SleepStatusChart,
    WellnessOverviewChart,
    DateSelector,
    TodayWellness,
    TeamPainWidget,
    MenstrualPhases,
    SicknessLabel,
    TableSelectionChipValue,
    TableBarValue,
    PainLabel
  },
  data: () => ({
    wellness: {
      data: {},
      loading: false
    },
    teamStats: {
      data: [],
      loading: false
    },
    playerPositions: {
      data: [],
      loading: false,
      selectedId: undefined
    },
    playerStatuses: {
      data: [],
      loading: false,
      selectedId: undefined
    },
    players: {
      data: [],
      loading: false,
      maxValues: {},
      minValues: {}
    },
    date: null
  }),
  computed: {
    RouteNames() {
      return RouteNames
    },
    filteredSearchedPlayers() {
      return this.players.data.filter(player => {
        return this.playerStatuses.selectedId ? player.player_status_id === this.playerStatuses.selectedId : true
      }).filter(player => {
        return this.playerPositions.selectedId ? player.player_positions.map(pos => pos.id).includes(this.playerPositions.selectedId) : true
      })
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    tableHeaders() {
      const headers = [
        { sortable: false, class: 'pr-0' },
        { key: 'name', align: 'start', text: this.$t('table_headers.name'), value: 'last_name', width: '15%' },
        { key: 'player_positions', text: this.$t('table_headers.player_overview.position'), value: 'player_positions', width: '150px' },
        { key: 'fatigue', text: this.$t('table_headers.player_overview.fatigue'), value: 'latest_stats.FAT' },
        { value: 'latest_stats.GMS', text: this.$t('table_headers.main_load.muscle_soreness'), tooltip: this.$t('table_headers.main_load.muscle_soreness.tooltip') },
        { value: 'latest_stats.STL', text: this.$t('table_headers.main_load.stress') },
        { value: 'latest_stats.MOO', text: this.$t('table_headers.main_load.mood') },
        { key: 'sleepStatus', text: this.$t('table_headers.main_load.sleep'), value: 'latest_stats.SLQ' },
        { key: 'sleepDuration', width: '40px', text: this.$t('table_headers.main_load.sleep_duration'), tooltip: this.$t('table_headers.main_load.sleep_duration.tooltip'), value: 'latest_stats.SLD' },
        { value: 'latest_stats.highest_pain', text: this.$t('table_headers.main_load.pain') },
        { value: 'latest_stats.sick', text: this.$t('table_headers.main_load.sickness') }
      ]

      if (this.showMenstrualPeriod) {
        headers.push({ value: 'latest_stats.FMENSACT', text: this.$t('table_headers.individual_load.menstrual_period') })
        headers.push({ value: 'latest_stats.cycle_day', text: this.$t('table_headers.individual_load.menstrual_cycle_phase') })
      }

      return headers
    },
    showMenstrualPeriod() {
      return this.players.data.some(p => p.gender === 'female')
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasSmartAssistantModule() {
      return this.user.organisation_settings.has_smart_assistant_module
    },
    latestTeamStats() {
      if (this.teamStats.data.length === 0) return {}

      return this.teamStats.data[this.teamStats.data.length - 1].stats
    },
    yesterdayTeamStats() {
      if (this.teamStats.data.length < 2) return {}

      return this.teamStats.data[this.teamStats.data.length - 2].stats
    }
  },
  watch: {
    date() {
      this.fetchPlayers()
      this.fetchTeamStats()
    },
    currentTeam(val) {
      if (val && val.id) {
        this.fetchPlayers()
        this.fetchTeamStats()
      }
    }
  },
  created() {
    this.noPhase = MenstrualPhase.NO_PHASE
    this.date = this.$dayjs()
    this.fetchPlayers()
    this.fetchTeamStats()
    this.fetchPlayerStatuses()
    this.fetchPlayerPositions()
  },
  methods: {
    get,
    fetchPlayers: debounce(async function() {
      try {
        this.players.loading = true

        const queryParams = {
          team_id: this.currentTeam.id === 'all' ? undefined : this.currentTeam.id,
          stats_date: this.$dayjs(this.date).format('YYYY-MM-DD')
        }

        const response = await PlayerApiService.getAll(queryParams)

        const players = response.data.map(player => {
          if (!player.latest_stats) {
            return player
          }

          if (player.latest_stats.SLD) player.latest_stats.SLD = (player.latest_stats.SLD / 60).toFixed(2)

          return player
        })

        this.players.maxValues = processMaxValues(players.map(player => player.latest_stats))
        this.players.minValues = processMinValues(players.map(player => player.latest_stats))

        this.players.data = players
      } catch (e) {
        this.players.data = []
        throw e
      } finally {
        this.players.loading = false
      }
    }, 50),
    fetchTeamStats: debounce(async function() {
      if (this.currentTeam.id === 'all' || !this.currentTeam.id) return

      this.teamStats.loading = true

      try {
        const teamStats = await TeamApiService.getStats(this.currentTeam.id, dayjs(this.date).subtract(7, 'days').format('YYYY-MM-DD'), dayjs(this.date).format('YYYY-MM-DD'))

        this.teamStats.data = teamStats.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.pulling_team_stats.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teamStats.loading = false
      }
    }),
    async fetchPlayerPositions() {
      try {
        this.playerPositions.loading = true

        const response = await PlayerPositionApiService.getAll({ sport_id: this.currentTeam.sport.id })

        this.playerPositions.data = response.data.map((position) => {
          return {
            id: position.id,
            name: this.$t(`player_position.${toCamelCase(position.name)}`),
            short: this.$t(`player_position.${toCamelCase(position.name)}.short`)
          }
        })
        this.playerPositions.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerPositions.loading = false
      }
    },
    async fetchPlayerStatuses() {
      try {
        this.playerStatuses.loading = true

        const response = await PlayerStatusApiService.getAll()

        this.playerStatuses.data = response.data.map((status) => {
          return {
            id: status.id,
            name: this.$t(`player_status.${status.name}`)
          }
        })
        this.playerStatuses.loaded = true
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_data.error'),
          color: 'error'
        })
      } finally {
        this.playerStatuses.loading = false
      }
    },
    navigateTo(playerId) {
      this.$router.push({ name: RouteNames.PLAYER_WELLNESS, params: { id: playerId } })
    }
  }
}
</script>

<style>

</style>
